import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PoundSterling, Check, X, Loader2, Users, ExternalLink } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import Avatar from "./Avatar";

/**
 * Admin-only card listing crowd-sourced price reports submitted via the
 * "Wrong price?" button on each campsite detail page. Items are grouped by
 * campsite, with a median + report count so the admin can quickly judge
 * consensus, then either apply (sets verified price) or dismiss.
 */
export default function PriceReportsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // campsite_id currently being mutated
  const [overrides, setOverrides] = useState({}); // campsite_id -> string price

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/price-reports?status=open");
      setItems(data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (campsiteId, price) => {
    const p = parseFloat(price);
    if (Number.isNaN(p) || p < 0 || p > 500) {
      toast.error("Enter a price between £0 and £500");
      return;
    }
    setBusy(campsiteId);
    try {
      await api.post(`/admin/price-reports/${campsiteId}/apply`, { price: p });
      toast.success(`Price set to £${p.toFixed(2)} and reports resolved`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (campsiteId) => {
    if (!window.confirm("Dismiss all open reports for this campsite?")) return;
    setBusy(campsiteId);
    try {
      const { data } = await api.post(`/admin/price-reports/${campsiteId}/dismiss`);
      toast.success(`Dismissed ${data.dismissed} report(s)`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="price-reports-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <PoundSterling className="w-3 h-3" /> Price Reports
          </div>
          <div className="font-display text-xl mb-1">Crowdsourced price corrections</div>
          <p className="text-sm text-[#555] max-w-2xl">
            Logged-in users flag wrong prices via the "Wrong price?" button on each
            campsite detail page. Reports are grouped by campsite, ordered by recency.
            Apply the median (or your own value) to set <code className="mono text-xs">price_per_night</code> and stamp <code className="mono text-xs">price_verified_at</code>.
          </p>
        </div>
        <div className="flex items-center gap-2 mono text-xs text-[#888]">
          <Users className="w-3 h-3" />
          {items.length} campsite(s) with open reports
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="p-8 border hairline bg-[#fafafa] text-center text-sm text-[#888]" data-testid="price-reports-empty">
          No open price reports. Quiet on the front lines.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it, idx) => {
            const overrideVal = overrides[it.campsite_id] ?? it.median_price?.toString() ?? "";
            return (
              <div key={it.campsite_id} className="p-4 border hairline bg-[#fafafa]" data-testid={`price-report-${idx}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <Link
                      to={`/campsites/${it.campsite_id}`}
                      className="font-display text-lg hover:underline inline-flex items-center gap-1"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {it.campsite_name || it.campsite_id}
                      <ExternalLink className="w-3 h-3 text-[#888]" />
                    </Link>
                    <div className="mono text-xs text-[#888]">{it.report_count} report(s) · latest {new Date(it.latest_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="eyebrow text-[#ff4500]">Median</div>
                    <div className="font-display text-2xl mono">£{it.median_price?.toFixed(2)}</div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="eyebrow mb-1.5">All submitted prices</div>
                  <div className="flex flex-wrap gap-2">
                    {it.reports.map((r) => (
                      <div
                        key={r.report_id}
                        className="text-xs mono px-2 py-1 bg-white border hairline flex items-center gap-1.5"
                        title={r.note || ""}
                      >
                        <Avatar src={r.user_picture} name={r.user_name} size={18} ringed={false} />
                        <span>£{r.correct_price.toFixed(2)} · {r.user_name}</span>
                        {r.note && <span className="text-[#888]"> — {r.note.slice(0, 40)}{r.note.length > 40 ? "…" : ""}</span>}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-xs">
                    <span className="eyebrow">Apply £</span>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      step="0.50"
                      value={overrideVal}
                      onChange={(e) => setOverrides({ ...overrides, [it.campsite_id]: e.target.value })}
                      className="w-24 border hairline px-2 py-1.5 text-sm focus:outline-none focus:border-[#ff4500] mono"
                      data-testid={`price-input-${idx}`}
                    />
                  </label>
                  <button
                    onClick={() => apply(it.campsite_id, overrideVal)}
                    disabled={busy === it.campsite_id}
                    className="btn-primary !px-3 !py-2 text-[11px] inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`apply-price-${idx}`}
                  >
                    {busy === it.campsite_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Apply & verify
                  </button>
                  <button
                    onClick={() => dismiss(it.campsite_id)}
                    disabled={busy === it.campsite_id}
                    className="btn-secondary !px-3 !py-2 text-[11px] inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`dismiss-price-${idx}`}
                  >
                    <X className="w-3 h-3" /> Dismiss all
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
